import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  BehaviorSubject, Observable, of, combineLatest, defer,
  catchError, filter, map, take, tap, throwError
} from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  WorkflowRequest,
  WorkflowPhase,
  nextPhase as computeNextPhase,
  previousPhase as computePreviousPhase
} from '../../objects/workflow';

/**
 * Service for the greenfield Workflow Requests API.
 *
 * Endpoints (base URL configured in environment as URL_WORKFLOW_CREATE/LIST/GET/UPDATE):
 *   POST  /api/{org}/workflow-requests                  → create
 *   GET   /api/{org}/workflow-requests?limit=&lastKey=  → list
 *   GET   /api/{org}/workflow-requests/{workflowNum}    → get one
 *   PUT   /api/{org}/workflow-requests/{workflowNum}    → update (including phase advance/retreat)
 *
 * Rules enforced by the backend:
 *   - Phase transitions must move ±1 step only. Skipping returns 400 "Cannot skip phases".
 *   - workflowNum is backend-generated on create (do not send it).
 *   - createdAt/updatedAt/phaseHistory are backend-managed (do not send them).
 *
 * See WORKFLOW_REQUESTS_README.md in this folder for full details.
 */
@Injectable({
  providedIn: 'root'
})
export class WorkflowRequestsService {
  private http = inject(HttpClient);
  private snackBar = inject(MatSnackBar);

  private allRequestsSubject = new BehaviorSubject<WorkflowRequest[]>([]);

  private isCacheRefreshedSubject = new BehaviorSubject<boolean>(false);
  public isCacheRefreshed$ = this.isCacheRefreshedSubject.asObservable();

  /** Guards against re-fetching on every new subscriber. */
  private loaded = false;
  private inFlight = false;

  /**
   * The request cache, loaded on FIRST SUBSCRIPTION rather than on construction.
   *
   * This service is `providedIn: 'root'`, so it is instantiated the first time
   * anything injects it — and `NewWorkflowRequestDialogComponent` injects it, which
   * means merely OPENING the Drop-and-Reveal dialog from the Contracts / Parties /
   * BAAs / COIs / People / Provisions / Reports sidenav used to fire a list GET.
   * When the org's `{org}-WorkflowRequests` table was missing, that GET 500'd and
   * `handleError` popped a red "Failed to list workflow requests" snackbar at users
   * who had not asked for anything to do with workflow.
   *
   * `defer` re-runs the factory per subscriber, and `ensureLoaded()` is flag-guarded,
   * so the fetch happens exactly once and only when someone actually reads the cache.
   * Subscribers still see a populated stream, so the `allRequests$ | async` binding in
   * workflows-list is unchanged — this is why the fix lives here rather than as
   * refresh() calls in each consumer's ngOnInit, which would silently regress the first
   * time someone adds a consumer and forgets.
   */
  public allRequests$: Observable<WorkflowRequest[]> = defer(() => {
    this.ensureLoaded();
    return this.allRequestsSubject.asObservable();
  });

  // ========================
  // Core API
  // ========================

  /**
   * Create a new workflow request. Do NOT send workflowNum, createdAt, updatedAt,
   * or phaseHistory — the backend owns these. Required: requestor and either
   * documentType or classification.
   */
  create(payload: Partial<WorkflowRequest>): Observable<{ workflowNum: string; workflowRequest: WorkflowRequest }> {
    const url = (environment as any).URL_WORKFLOW_CREATE;
    // Strip fields the backend owns
    const { workflowNum, createdAt, updatedAt, phaseHistory, ...body } = payload;
    return this.http.post<{ workflowNum: string; message: string; workflowRequest: WorkflowRequest }>(url, body).pipe(
      tap(() => this.refreshAllRequests()),
      map(res => ({ workflowNum: res.workflowNum, workflowRequest: res.workflowRequest })),
      catchError(err => this.handleError(err, 'create workflow request'))
    );
  }

  /**
   * List workflow requests, paginated.
   * lastEvaluatedKey === null in the response means end of results.
   *
   * `silent` suppresses the error snackbar. Background cache refreshes pass true:
   * the user did not ask for a list, so a toast about one is noise at best and
   * misleading at worst. User-initiated calls leave it false and keep the message.
   */
  list(
    opts?: { limit?: number; lastEvaluatedKey?: string | null },
    silent = false
  ): Observable<{
    requests: WorkflowRequest[];
    lastEvaluatedKey: string | null;
    count: number;
  }> {
    const url = (environment as any).URL_WORKFLOW_LIST;
    const params: any = {};
    if (opts?.limit) params.limit = String(opts.limit);
    if (opts?.lastEvaluatedKey) params.lastEvaluatedKey = opts.lastEvaluatedKey;
    return this.http.get<{ requests: WorkflowRequest[]; lastEvaluatedKey: string | null; count: number }>(url, { params }).pipe(
      catchError(err => this.handleError(err, 'list workflow requests', silent))
    );
  }

  /**
   * Get one workflow request by its workflowNum. Errors on 404.
   */
  get(workflowNum: string): Observable<WorkflowRequest> {
    const url = (environment as any).URL_WORKFLOW_GET + workflowNum;
    return this.http.get<WorkflowRequest>(url).pipe(
      catchError(err => this.handleError(err, 'get workflow request'))
    );
  }

  /**
   * Partial update. Only send fields that changed. When changing phase, include
   * `requestor` so the backend can stamp enteredBy in phaseHistory.
   *
   * Do NOT send workflowNum (in the URL), createdAt, updatedAt, or phaseHistory
   * — backend owns these.
   */
  update(workflowNum: string, patch: Partial<WorkflowRequest>): Observable<{ workflowNum: string }> {
    const url = (environment as any).URL_WORKFLOW_UPDATE + workflowNum;
    // Strip backend-owned fields
    const { workflowNum: _, createdAt, updatedAt, phaseHistory, ...body } = patch;
    return this.http.put<{ workflowNum: string; message: string }>(url, body).pipe(
      tap(() => this.refreshAllRequests()),
      map(res => ({ workflowNum: res.workflowNum })),
      catchError(err => this.handleError(err, 'update workflow request'))
    );
  }

  // ========================
  // Convenience wrappers
  // ========================

  /**
   * Advance the workflow one phase. Uses the current phase to compute the next.
   * Returns null (as Observable) if already at the last phase.
   */
  advancePhase(workflowNum: string, currentPhase: string, requestor: string): Observable<{ workflowNum: string; newPhase: WorkflowPhase } | null> {
    const newPhase = computeNextPhase(currentPhase);
    if (!newPhase) {
      this.snackBar.open('Already at the final phase.', 'OK', { duration: 3000 });
      return of(null);
    }
    return this.update(workflowNum, { phase: newPhase, requestor }).pipe(
      map(res => ({ workflowNum: res.workflowNum, newPhase }))
    );
  }

  /**
   * Retreat the workflow one phase. Uses the current phase to compute the previous.
   * Returns null (as Observable) if already at the first phase.
   */
  retreatPhase(workflowNum: string, currentPhase: string, requestor: string): Observable<{ workflowNum: string; newPhase: WorkflowPhase } | null> {
    const newPhase = computePreviousPhase(currentPhase);
    if (!newPhase) {
      this.snackBar.open('Already at the first phase.', 'OK', { duration: 3000 });
      return of(null);
    }
    return this.update(workflowNum, { phase: newPhase, requestor }).pipe(
      map(res => ({ workflowNum: res.workflowNum, newPhase }))
    );
  }

  // ========================
  // Cache access (for list view)
  // ========================

  /**
   * Load the cache once, on first read. No-op once loaded or while a load is in
   * flight. Only `allRequests$` calls this — everything else that wants fresh data
   * calls refreshAllRequests() directly.
   */
  private ensureLoaded(): void {
    if (this.loaded || this.inFlight) return;
    this.refreshAllRequests();
  }

  /**
   * Refresh the in-memory cache used by allRequests$. Called on first read of the
   * cache and unconditionally after every create/update (which must bypass the
   * `loaded` flag to pick up the change they just made).
   *
   * Errors are logged, never toasted — see list()'s `silent` parameter.
   */
  refreshAllRequests(): void {
    // Orgs without URL_WORKFLOW_* configured would otherwise pass `undefined` to
    // http.get() and issue a request against the app's own origin. The keys are read
    // via `(environment as any)`, so a missing key is not a compile error — it is
    // absent from 15 of the 23 env files, four of which still set
    // workflowManagement: true. Settle the cache as empty and say so once.
    if (!(environment as any).URL_WORKFLOW_LIST) {
      console.warn('[WorkflowRequestsService] URL_WORKFLOW_LIST is not configured for this org — workflow cache stays empty.');
      this.loaded = true;
      this.allRequestsSubject.next([]);
      this.isCacheRefreshedSubject.next(true);
      return;
    }

    this.inFlight = true;
    this.list({ limit: 500 }, true).subscribe({
      next: res => {
        this.inFlight = false;
        this.loaded = true;
        this.allRequestsSubject.next(res.requests || []);
        this.isCacheRefreshedSubject.next(true);
      },
      error: err => {
        this.inFlight = false;
        // Deliberately NOT setting `loaded` — a failed load should be retried by the
        // next subscriber rather than cached as "empty forever".
        console.error('[WorkflowRequestsService] refreshAllRequests failed:', err);
        this.allRequestsSubject.next([]);
        this.isCacheRefreshedSubject.next(true);
      }
    });
  }

  /**
   * Get a cached request from allRequests$ (returns null if not in cache).
   * For an authoritative fetch, use get() instead.
   */
  getRequestByNum(workflowNum: string): Observable<WorkflowRequest | null> {
    return this.allRequests$.pipe(
      map(requests => requests.find(r => r.workflowNum === workflowNum) || null)
    );
  }

  /**
   * Get cached requests linked to a specific entity (returns [] if none).
   *
   * Emits ONCE and completes, rather than staying open over the BehaviorSubject.
   * The previous version never completed, so `workflow-connections.component.ts`
   * (which subscribes without teardown) leaked, and it also emitted the initial
   * `[]` seed before the fetch landed — flipping the component's `loading` flag
   * off early and flashing "No workflow requests yet" at a user whose requests
   * were still in flight. Gating on isCacheRefreshed$ waits for real data.
   */
  getRequestsByEntityId(entityId: string): Observable<WorkflowRequest[]> {
    return combineLatest([this.allRequests$, this.isCacheRefreshed$]).pipe(
      filter(([, refreshed]) => refreshed),
      take(1),
      map(([requests]) => requests.filter(r => r.entityId === entityId))
    );
  }

  // ========================
  // Error mapping
  // ========================

  private handleError(err: HttpErrorResponse, context: string, silent = false): Observable<never> {
    const status = err?.status;
    const serverMsg = err?.error?.error || err?.error?.message || err?.message || 'Unknown error';

    if (silent) {
      console.error(`[WorkflowRequestsService] ${context} failed (silent):`, err);
      return throwError(() => err);
    }

    if (status === 400 && /skip/i.test(serverMsg)) {
      this.snackBar.open('Cannot skip workflow phases — advance one step at a time.', 'OK', { duration: 5000 });
    } else if (status === 404 && context === 'update workflow request') {
      this.snackBar.open('This request no longer exists — refresh the list.', 'OK', { duration: 5000 });
    } else if (status === 404 && context === 'get workflow request') {
      this.snackBar.open('Workflow request not found.', 'OK', { duration: 4000 });
    } else {
      this.snackBar.open(`Failed to ${context}: ${serverMsg}`, 'OK', { duration: 5000 });
    }

    console.error(`[WorkflowRequestsService] ${context} failed:`, err);
    return throwError(() => err);
  }
}
